import { AsyncPipe, DecimalPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, ChangeDetectorRef, AfterViewInit, input, inject } from '@angular/core';
import { ReactiveFormsModule, FormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { RefundService, ToastMessageService } from '@fitness-force/api';
import { AppErrors ,PdfType} from '@fitness-force/models';
import { ErrorMsgComponent } from '@fitness-force/errors';;
import { DataGridComponent, EmptyStateContainerComponent } from '@fitness-force/grid';
import { DateFormatedComponent } from '@fitness-force/grid-utility';
import { ActionsComponent } from '@fitness-force/grid-utility/action';
import { CurrencyComponent } from '@fitness-force/grid-utility/currency';
import type { Claim, TENANTSINTERFACE, voidSalesReturnRequest, Refund, RefundList } from '@fitness-force/models';
import { NumberPrefixComponent } from '@fitness-force/common-number-prefix';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TENANTS_INFO } from '@fitness-force/state';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { SelectModule } from 'primeng/select';
import { PopoverModule } from 'primeng/popover';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TimelineModule } from 'primeng/timeline';
import { BehaviorSubject, Observable } from 'rxjs';
import { RefundStatusComponent } from '../refund-status/refund-status.component';
import { SkeletonModule } from 'primeng/skeleton';
import { PaymentModeOtherComponent, StaffInfoComponent } from '@fitness-force/shared';

@Component({
  selector: 'refund-tab',
  templateUrl: './refund-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    DividerModule,
    TimelineModule,
    TableModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    RippleModule,
    SelectModule,
    AvatarModule,
    TagModule,
    StyleClassModule,
    DialogModule,
    PdfViewerModule,
    PopoverModule,
    DataGridComponent,
    ErrorMsgComponent,
    SkeletonModule,
    TranslateModule, AsyncPipe, DecimalPipe, NgClass],
})
export class RefundListComponent implements OnInit, AfterViewInit {
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly refundService = inject(RefundService);
  private readonly toastService = inject(ToastMessageService);
  private readonly cd = inject(ChangeDetectorRef);


  readonly clientId = input(0);
  readonly tenantId = input(0);
  readonly subscriptionId = input(0);
  readonly showTableActions = input(true);
  readonly showHeader = input(true);

  RefundData$ = new BehaviorSubject<Refund[]>([] as Refund[]);
  RefundTablecolumns$ = new BehaviorSubject<any>([]);
  RefundClientDataObject: BehaviorSubject<Refund> | any;
  selectallRefundData = false;

  claim$: Observable<Claim[]> = new Observable();
  _filterStaff = new BehaviorSubject<number[]>([0]);
  _sessionOwner = new BehaviorSubject<number[]>([0]);
  accessedTenants:any;
  btnLoading=false;
  emptyStateComponent$ = new BehaviorSubject<any>(EmptyStateContainerComponent);
  showBulkActionBar: boolean=false;
  RefundId:number=0;
  RefundDetails!: Refund;
  showRefundVoid:boolean=false;
  voidRefundForm: UntypedFormGroup = new UntypedFormGroup({});
  number_prefix:string="";
  showInvoicePopup:boolean=false;

  showRefundPopup = new BehaviorSubject(false);
  pdfSrc = new BehaviorSubject<string>('');
  loading:boolean = false;

  ngOnInit(): void {
    this.store.select(TENANTS_INFO).subscribe((tenant: TENANTSINTERFACE) => {
      this.accessedTenants = tenant.accessed_tenant;
    });
    this.getRefund();
  }

  ngAfterViewInit() {
    this.voidRefundForm = this.fb.group({
      void_reason: ['', [Validators.required]],
    });
  }

  getRefund() {
    this.loading = true;
    this.refundService.getRefund(this.clientId()).subscribe({
      next: (data: RefundList) => {
        this.setClientRefunds(data.items ? data.items : []);
        this.loading = false;
      },
      error: () => {
        this.setClientRefunds([]);
        this.loading = false;
      },
    });
  }

  setClientRefunds(Refunds: Refund[]) {
    this.RefundData$.next(Refunds);
    this.selectallRefundData = false;
    this.GetRefundTableColumns();
  }

  GetRefundTableColumns() {
    this.translate
      .get([
        'REFUND.COLUMNS.DATE',
        'REFUND.COLUMNS.ID',
        'REFUND.COLUMNS.PAYMENT',
        'REFUND.COLUMNS.REFUND_AMOUNT',
        'PAYMENT.COLUMNS.PAYMENT_MODE',
        'REFUND.COLUMNS.STATUS',
        'REFUND.COLUMNS.REFUNDED_BY',
        'REFUND.COLUMNS.REFUND_COMMENT'
  ])
      .subscribe((translations) => {
        this.ColumnsInvoiceTableTransalation(translations);
      });
  }

  ColumnsInvoiceTableTransalation(translations: any) {
    const Columns = {
      DATE: translations['REFUND.COLUMNS.DATE'],
      PAYMENT: translations[ 'REFUND.COLUMNS.PAYMENT'],
      ID: translations[ 'REFUND.COLUMNS.ID'],
      AMOUNT: translations['REFUND.COLUMNS.REFUND_AMOUNT'],
      PAYMENT_MODE: translations['PAYMENT.COLUMNS.PAYMENT_MODE'],
      STATUS: translations['REFUND.COLUMNS.STATUS'],
      REFUNDED_BY: translations['REFUND.COLUMNS.REFUNDED_BY'],
      REFUND_COMMENT: translations['REFUND.COLUMNS.REFUND_COMMENT']
    };

    this.RefundTablecolumns$.next([
      {
        title: Columns.DATE,
        classes: ' py-3 text-600 w-15rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'refund_date',
        type: 'component',
        data: {
          name: DateFormatedComponent,
          additionData: true,
          aData: { KeyName: 'refund_date', format: 'd MMM y' },
        },
      },
      {
        title: Columns.ID,
        classes: 'py-3 white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'refund_prefix',
        type: 'component',
        data: {  
          name: NumberPrefixComponent,
          additionData: true,
          aData: { fetch_key: 'refund_prefix', type: PdfType.Refund,id:'id' }
        },
      },
      {
        title: Columns.PAYMENT,
        classes: 'py-3 white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'payment_prefix',
        type: 'component',
        data: {  
          name: NumberPrefixComponent,
          additionData: true,
          aData: { fetch_key: 'payment_prefix', type: PdfType.Payment,id:'payment_id', status:'payment_status' }
        },
      },
      {
        title: Columns.AMOUNT,
        classes: ' py-3 text-600 w-15rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'amount_in_minor_currency',
        type: 'component',
        data: {
          name: CurrencyComponent,
          fetch_key: 'amount_in_minor_currency',
        },
      },
      {
        title: Columns.PAYMENT_MODE,
        classes: ' py-2 text-600 w-15rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        type: 'component',
        dataProperty: 'payment_mode_level2_id',
        data: {
          name: PaymentModeOtherComponent,
          additionData: true,
          aData: { fetch_key: 'payment_mode_level2_id' },
        },
      },  
      {
        title: Columns.STATUS,
        classes: ' py-3 text-600 w-15rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'refund_status',
        type: 'component',
        data: {
          name: RefundStatusComponent,
          additionData: true,
          aData: { fetch_key: 'refund_status' },
        }
      },
      {
        title: Columns.REFUND_COMMENT,
        classes: ' py-3 text-600 w-15rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'refund_comments',
        type: 'default',
      },
      {
        title: Columns.REFUNDED_BY,
        classes: ' py-3 text-600 w-15rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'created_by',
        type: 'component',
        data: {
          name: StaffInfoComponent,
          fetch_key: 'created_by',
          additionData: true,
          aData: { fetch_key: 'created_by' },
        }
      }
    ]);

    if (this.showTableActions()) {
      this.RefundTablecolumns$.value.push({
        title: '',
        classes: 'py-2 white-space-nowrap',
        dataProperty: 'actions',
        type: 'component',
        data: {
          name: ActionsComponent,
          additionData: true,
          aData: [
            'refundvoid',
            'sendemail',
            'print'
          ],
        },
      });
    }
  }

  handleSelectAll(selection: any) {
    if (selection.checked) {
      this.showBulkActionBar = true;
    } else {
      this.showBulkActionBar = false;
    }
  }

  handleEvent(e: any) {}

  handleTabAction(event:{closePopup:boolean, reloadData:boolean})
  {
    console.log(event);
  }

  onActionEvent(event: any) {
    switch (event.action) {
      case 'refundvoid':
        {
          this.RefundId = event.columnData.id;
          this.RefundDetails = event.columnData;
          this.showRefundVoid=true;
        }
       break;
      case 'sendemail':
        {
          this.RefundDetails = event.columnData;
          this.SendRefundPdf(false);
        }
        break;
      case 'print':
        {
          this.RefundId = event.columnData.id;
          this.RefundDetails = event.columnData;
          this.GetRefundPdf(false).then(() => {
            this.printInvoice();
          });
        }
        break;
  }
  }


  GetRefundPdf(showRefund: boolean) {
    const promise: Promise<boolean> = new Promise((resolve, reject) => {
      this.refundService.GetRefundPrint(this.clientId(),this.RefundDetails.id)
        .subscribe({
          next: (data: any) => {
            this.pdfSrc.next(data.url);
            resolve(true);
          },
          error: (error: AppErrors) => {
              this.ShowToastErrors(error);
              resolve(false);
          },
        });
    });
    return promise;
  }

  SendRefundPdf(showRefund: boolean) {
    const promise: Promise<boolean> = new Promise((resolve, reject) => {
      const channels:any[]=[];
      channels.push("Email");
      this.refundService.SendRefundPdf(this.clientId(),this.RefundDetails.id,channels)
        .subscribe({
          next: (data: any) => {
            this.pdfSrc.next(data);
            this.translate
            .get('TRANSACTION.MSG.INVOICE_MAIL_MSG')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
            resolve(true);
          },
          error: (error: AppErrors) => {
            if(error.originalError.status == 200)
            {
                this.toastMessageService.showSuccess(error.originalError.error.text);
                resolve(true);
            }
            else{
              this.ShowToastErrors(error);
              resolve(false);
            }
           
          },
        });
    });
    return promise;
  }

  printInvoice() {
    window.open(this.pdfSrc.value, '_blank');
  }

  deleteRefund(form: UntypedFormGroup) {
    this.btnLoading= true;
    if (form.valid) {
      const voidRequest: voidSalesReturnRequest = {} as voidSalesReturnRequest;
      voidRequest.void_reason = form.value.void_reason;
      let tenantId=0;
      if(this.accessedTenants.find((o: { id: number; }) => o.id == this.RefundDetails.tenant_id))
         tenantId =  this.RefundDetails.tenant_id;
      else tenantId= this.tenantId();
      this.refundService.VoidRefund(tenantId, this.clientId(), this.RefundId, voidRequest)
        .subscribe({
          next: () => {
            this.translate
              .get('REFUND.MSG.REFUND_VOID_MSG')
              .subscribe((translation) => {
                this.toastService.showSuccess(translation);
              });
            this.voidRefundForm.reset();
            this.RefundDetails.refund_isvoid = true;
            const RefundData = this.RefundData$.value;
            RefundData.forEach((inv) => {
              if (inv.id == this.RefundDetails.id)
                inv.refund_isvoid  = true;
            });
            this.RefundData$.next(RefundData);
            this.GetRefundTableColumns();
            this.btnLoading= false;
            this.showRefundVoid = false;
            this.cd.detectChanges();
          },
          error: (error: AppErrors) => {
            this.btnLoading= false;
            this.toastService.showError(error);
            this.cd.detectChanges();
          },
        });
    } else {
      Object.keys(this.voidRefundForm.controls).forEach((field) => {
        const control = this.voidRefundForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
      this.btnLoading= false;
    }
  }

  ShowToastErrors(error: any) {
   this.toastService.showError(error);
  }

  closeInvoicePopup(event:any)
  {
    this.showInvoicePopup=false;
  }

  closeRefundPopup()
  {
    this.showRefundPopup.next(false);
  }
  
  get VoidReasonFormControl(): UntypedFormControl {
    return this.voidRefundForm.controls['void_reason'] as UntypedFormControl;
  }


}
